import { defineStore } from "pinia";
import AdminNotificationService from "@/services/adminNotification.service";

let fetchPromise = null;
let initPromise = null;

function extractNotifications(response) {
  const notifications = response?.data?.data;
  return Array.isArray(notifications) ? notifications : [];
}

function extractPagination(response) {
  return response?.data?.pagination || null;
}

function extractUnreadCount(response) {
  if (typeof response?.data === "number") {
    return response.data;
  }

  if (typeof response?.data === "string" && response.data.trim() !== "") {
    return Number(response.data);
  }

  if (typeof response?.data?.data === "number") {
    return response.data.data;
  }

  if (
    typeof response?.data?.data === "string" &&
    response.data.data.trim() !== ""
  ) {
    return Number(response.data.data);
  }

  return 0;
}

function mergeNotifications(existingNotifications, incomingNotifications) {
  const notificationMap = new Map();

  existingNotifications.forEach((notification) => {
    notificationMap.set(notification.id, notification);
  });

  incomingNotifications.forEach((notification) => {
    notificationMap.set(notification.id, notification);
  });

  return Array.from(notificationMap.values());
}

export const useNotificationStore = defineStore("notification", {
  state: () => ({
    notifications: [],
    unreadCount: 0,
    loading: false,
    loadingMore: false,
    initialized: false,
    currentPage: 1,
    perPage: 10,
    hasMore: true,
  }),

  actions: {
    async fetchUnreadCount() {
      try {
        const response = await AdminNotificationService.getUnreadCount();
        this.unreadCount = extractUnreadCount(response);
        return this.unreadCount;
      } catch (error) {
        console.error("Unread notifications count error:", error);
        throw error;
      }
    },

    async fetchNotifications({ reset = true } = {}) {
      if (fetchPromise) {
        return fetchPromise;
      }

      fetchPromise = (async () => {
        const page = reset ? 1 : this.currentPage;

        if (reset) {
          this.loading = true;
        } else {
          this.loadingMore = true;
        }

        try {
          const response = await AdminNotificationService.getAll({
            per_page: this.perPage,
            page,
          });
          const notifications = extractNotifications(response);
          const pagination = extractPagination(response);

          if (reset) {
            this.notifications = notifications;
          } else {
            this.notifications = mergeNotifications(
              this.notifications,
              notifications
            );
          }

          if (pagination) {
            this.currentPage = pagination.current_page + 1;
            this.hasMore =
              pagination.current_page < pagination.total_pages &&
              Boolean(pagination.links?.next);
          } else {
            this.currentPage = page + 1;
            this.hasMore = notifications.length === this.perPage;
          }

          await this.fetchUnreadCount();

          return notifications;
        } catch (error) {
          console.error("Notifications Error:", error);
          throw error;
        } finally {
          this.loading = false;
          this.loadingMore = false;
          fetchPromise = null;
        }
      })();

      return fetchPromise;
    },

    async markAsRead(id) {
      try {
        await AdminNotificationService.markAsRead(id);

        const notification = this.notifications.find((item) => item.id === id);

        if (!notification || notification.is_read) {
          return;
        }

        notification.is_read = true;
        this.unreadCount = Math.max(this.unreadCount - 1, 0);
      } catch (error) {
        console.error("Mark notification as read error:", error);
        throw error;
      }
    },

    async markAllAsRead() {
      try {
        await AdminNotificationService.markAllAsRead();

        this.notifications = this.notifications.map((notification) => ({
          ...notification,
          is_read: true,
        }));
        this.unreadCount = 0;

        return this.notifications;
      } catch (error) {
        console.error("Mark all notifications as read error:", error);
        throw error;
      }
    },

    async init() {
      if (this.initialized) {
        return this.notifications;
      }

      if (initPromise) {
        return initPromise;
      }

      initPromise = (async () => {
        try {
          const notifications = await this.fetchNotifications({ reset: true });
          this.initialized = true;
          return notifications;
        } finally {
          initPromise = null;
        }
      })();

      return initPromise;
    },

    reset() {
      fetchPromise = null;
      initPromise = null;
      this.notifications = [];
      this.unreadCount = 0;
      this.loading = false;
      this.loadingMore = false;
      this.initialized = false;
      this.currentPage = 1;
      this.perPage = 10;
      this.hasMore = true;
    },

    async loadMoreNotifications() {
      if (!this.hasMore || this.loading || this.loadingMore) {
        return this.notifications;
      }

      return this.fetchNotifications({ reset: false });
    },
  },
});
